import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useShift } from '@/hooks/useShift';
import { supabase } from '@/integrations/supabase/client';
import { captureEvent } from '@/lib/posthog';
import { AppLayout } from '@/components/layout/AppLayout';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Camera, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { compressImageSafe } from '@/lib/image-utils';

export default function RegisterIncidentPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id: patientId } = useParams<{ id: string }>();
    const { user } = useAuth();
    const { activeShift } = useShift();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const INCIDENT_TYPES = [
        { value: 'fall', labelKey: 'incidents.fall', emoji: '🤕' },
        { value: 'vomit', labelKey: 'incidents.vomit', emoji: '🤢' },
        { value: 'agitation', labelKey: 'incidents.agitation', emoji: '😤' },
        { value: 'other', labelKey: 'incidents.other', emoji: '⚠️' }
    ];

    const [incidentType, setIncidentType] = useState<string>('fall');
    const [description, setDescription] = useState('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const { file: compressedFile, previewUrl } = await compressImageSafe(file, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.7
            });

            setPhotoFile(compressedFile);
            setPhotoPreview(previewUrl);
        } catch (error) {
            console.error('Error processing incident photo:', error);
            toast.error(t('common.error'));
        }
    };

    const uploadPhoto = async (): Promise<string | null> => {
        if (!photoFile || !user || !patientId) return null;

        const timestamp = Date.now();
        // First folder MUST be patient_id (shift-photos RLS policy).
        const filePath = `${patientId}/${user.id}/incident-${timestamp}.jpg`;

        const { error: uploadError } = await supabase.storage
            .from('shift-photos')
            .upload(filePath, photoFile, { contentType: photoFile.type || 'image/jpeg', upsert: false });

        if (uploadError) {
            console.error('Upload error:', uploadError);
            toast.error(t('common.error'));
            return null;
        }

        const { data } = supabase.storage
            .from('shift-photos')
            .getPublicUrl(filePath);

        return data.publicUrl;
    };

    const handleSubmit = async () => {
        if (!user || !patientId) return;

        if (!description.trim()) {
            toast.error(t('incidents.descriptionRequired'));
            return;
        }

        setIsSubmitting(true);

        try {
            let photoUrl: string | null = null;
            if (photoFile) {
                photoUrl = await uploadPhoto();
            }

            const incidentName = INCIDENT_TYPES.find(it => it.value === incidentType)?.labelKey
                ? t(INCIDENT_TYPES.find(it => it.value === incidentType)!.labelKey)
                : t('incidents.title');

            const { error: execError } = await supabase
                .from('task_executions')
                .insert({
                    task_id: null,
                    patient_id: patientId,
                    executed_by: user.id,
                    shift_id: activeShift?.id || null,
                    photo_url: photoUrl,
                    notes: description.trim(),
                    is_alert: true,
                    is_sporadic: true,
                    adhoc_task_type: 'incident',
                    adhoc_name: incidentName,
                });

            if (execError) throw execError;

            toast.success(t('incidents.success'));
            toast.warning(t('incidents.familyNotified'));

            // `incident_registered` is emitted server-side (trigger on task_executions).

            navigate(`/app/caregiver/${patientId}`);

        } catch (error: any) {
            console.error('Error recording incident:', error);
            toast.error(error.message || t('incidents.errorRegistering'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AppLayout
            title={t('incidents.registerTitle')}
            showBack
            onBack={() => navigate(`/app/caregiver/${patientId}`)}
        >
            <div className="p-4 space-y-4 pb-24">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-destructive" />
                            {t('incidents.incidentType')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <RadioGroup
                            value={incidentType}
                            onValueChange={setIncidentType}
                            className="grid grid-cols-2 gap-2"
                        >
                            {INCIDENT_TYPES.map((type) => (
                                 <div key={type.value} className="h-full">
                                     <RadioGroupItem
                                         value={type.value}
                                         id={type.value}
                                         className="peer sr-only"
                                     />
                                     <Label
                                         htmlFor={type.value}
                                         className={cn(
                                             "flex h-full flex-col items-center justify-center gap-2 p-4 rounded-lg border-2 cursor-pointer transition-all",
                                            incidentType === type.value
                                                ? "border-destructive bg-destructive/10 text-destructive"
                                                : "border-border bg-card hover:border-destructive/50"
                                        )}
                                    >
                                        <span className="text-2xl">{type.emoji}</span>
                                        <span className="text-sm font-medium text-center whitespace-normal leading-tight break-words">{t(type.labelKey)}</span>
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t('incidents.description')} *</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            placeholder={t('incidents.descriptionPlaceholder')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className="resize-none"
                        />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Camera className="w-4 h-4" />
                            {t('incidents.photo')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={handlePhotoChange}
                        />

                        {photoPreview ? (
                            <div className="relative">
                                <img
                                    src={photoPreview}
                                    alt="Preview"
                                    className="w-full aspect-video object-cover rounded-lg"
                                />
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    className="absolute bottom-2 right-2"
                                    onClick={() => {
                                        setPhotoFile(null);
                                        setPhotoPreview(null);
                                        if (fileInputRef.current) fileInputRef.current.value = '';
                                    }}
                                >
                                    {t('incidents.retake')}
                                </Button>
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                className="w-full h-32 flex flex-col gap-2"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Camera className="w-8 h-8" />
                                <span>{t('incidents.takePhoto')}</span>
                            </Button>
                        )}
                    </CardContent>
                </Card>

                <Button
                    className="w-full bg-destructive hover:bg-destructive/90"
                    size="lg"
                    disabled={isSubmitting || !description.trim()}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('incidents.registering')}
                        </>
                    ) : (
                        t('incidents.register')
                    )}
                </Button>
            </div>
        </AppLayout>
    );
}
